import {
  Avatar,
  AvatarFallbackText,
  AvatarImage,
} from "@/components/ui/avatar";
import { Text } from "@/components/ui/text";
import { Button, ButtonIcon, ButtonText } from "@/components/ui/button";
import { HStack } from "@/components/ui/hstack";
import { VStack } from "@/components/ui/vstack";
import { PaymentRuletDTODataDistribution } from "@/generated/api/models";
import { Hash, MoveRight, TriangleAlert, Wallet } from "lucide-react-native";
import { ReactNode, useMemo, useState } from "react";
import { ViewProps } from "react-native";
import { getName } from "utils/name";
import { CloseIcon, Icon } from "@/components/ui/icon";
import { Badge, BadgeText } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import {
  Modal,
  ModalBackdrop,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
} from "@/components/ui/modal";
import { Heading } from "@/components/ui/heading";
import { Box } from "@/components/ui/box";
import {
  PaymentVersionedRuleFromTo,
  RuleFromToDataProps,
} from "./restaurant.tipssharing.types";

function DeletedTooltipHandle({
  type,
}: {
  type: PaymentVersionedRuleFromTo["type"] | "qr";
}) {
  const [isTooltipOpen, setTooltipOpen] = useState(false);

  const entity = (() => {
    switch (type) {
      case "qr":
        return "QR-code";
      case "organization":
        return "business";
      case "participant":
        return "worker";
      case "tag":
        return "tag";
      case "wallet":
        return "wallet";
    }
  })();

  return (
    <>
      <Button
        variant="link"
        onPress={() => setTooltipOpen(true)}
        className="p-1"
      >
        <ButtonIcon as={TriangleAlert} className="stroke-red-500" />
      </Button>
      <Modal
        isOpen={isTooltipOpen}
        onClose={() => {
          setTooltipOpen(false);
        }}
        size="md"
      >
        <ModalBackdrop />
        <ModalContent>
          <ModalHeader>
            <Heading size="md" className="text-typography-950">
              {String(entity[0]).toUpperCase() + String(entity).slice(1)} is
              unavailable
            </Heading>
            <ModalCloseButton>
              <Icon
                as={CloseIcon}
                size="md"
                className="stroke-background-400 group-[:hover]/modal-close-button:stroke-background-700 group-[:active]/modal-close-button:stroke-background-900 group-[:focus-visible]/modal-close-button:stroke-background-900"
              />
            </ModalCloseButton>
          </ModalHeader>
          <ModalBody>
            <Text>
              This {entity} is unavailable. Perhaps it has been deleted
              recently. Please make a correction.
            </Text>
          </ModalBody>
          <ModalFooter>
            <Button
              onPress={() => {
                setTooltipOpen(false);
              }}
            >
              <ButtonText>Understood</ButtonText>
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
}

type RuleFromToBaseProps = RuleFromToDataProps & ViewProps;

function RuleFromToTag({
  fromTo,
  qrs,
  organization,
  wallets,
  tags,
  participants,
  ...rest
}: RuleFromToBaseProps & {
  fromTo: Extract<PaymentVersionedRuleFromTo, { type: "tag" }>;
}) {
  const unique = useMemo(
    () => new Set([...tags.private, ...tags.public]),
    [tags.private, tags.public]
  );
  const isDeleted = !unique.has(fromTo.tag);

  return (
    <HStack className="items-center justify-start gap-2" {...rest}>
      {isDeleted ? (
        <DeletedTooltipHandle type="tag" />
      ) : (
        <Avatar size="sm">
          <Icon as={Hash} size="sm" className="stroke-white" />
        </Avatar>
      )}
      <VStack className="items-start flex-1 overflow-hidden">
        <Badge action={isDeleted ? "error" : "info"}>
          <BadgeText>{isDeleted ? "Deleted tag" : fromTo.tag}</BadgeText>
        </Badge>
      </VStack>
    </HStack>
  );
}

function RuleFromToOrganization({
  fromTo,
  qrs,
  organization,
  wallets,
  tags,
  participants,
  ...rest
}: RuleFromToBaseProps & {
  fromTo: Extract<PaymentVersionedRuleFromTo, { type: "organization" }>;
}) {
  const qr = fromTo.qrId ? qrs.find((w) => w.id === fromTo.qrId) : undefined;

  return (
    <HStack className="items-center justify-start gap-2" {...rest}>
      {!!fromTo.qrId && !qr ? (
        <DeletedTooltipHandle type="qr" />
      ) : (
        <Avatar size="sm">
          <AvatarFallbackText>{organization.name}</AvatarFallbackText>
          <AvatarImage
            source={{ uri: organization.avatarImage ?? undefined }}
          />
        </Avatar>
      )}
      <VStack className="items-start flex-1 overflow-hidden">
        <Text
          size="sm"
          className="font-semibold text-typography-900 text-sm text-ellipsis whitespace-nowrap line-clamp-1"
          numberOfLines={1}
        >
          {organization.name}
        </Text>
        <Text
          size="xs"
          className="text-ellipsis whitespace-nowrap line-clamp-1"
          numberOfLines={1}
        >
          {fromTo.qrId
            ? `QR code: ${qr?.internalCode ?? "Deleted"}`
            : "Business"}
        </Text>
      </VStack>
    </HStack>
  );
}

function RuleFromToWallet({
  fromTo,
  qrs,
  organization,
  wallets,
  tags,
  participants,
  ...rest
}: RuleFromToBaseProps & {
  fromTo: Extract<PaymentVersionedRuleFromTo, { type: "wallet" }>;
}) {
  const wallet = useMemo(
    () => wallets.find((w) => w.id === fromTo.id),
    [wallets, fromTo.id]
  );

  return (
    <HStack className="items-center justify-start gap-2" {...rest}>
      {!wallet ? (
        <DeletedTooltipHandle type="wallet" />
      ) : (
        <Avatar size="sm">
          <Icon as={Wallet} size="sm" className="stroke-white" />
        </Avatar>
      )}
      <VStack className="items-start flex-1 overflow-hidden">
        <Text
          size="sm"
          className="font-semibold text-typography-900 text-sm text-ellipsis whitespace-nowrap line-clamp-1"
          numberOfLines={1}
        >
          {wallet ? wallet.name ?? wallet.currency : "Deleted wallet"}
        </Text>
        <Text
          size="xs"
          className="text-ellipsis whitespace-nowrap line-clamp-1"
          numberOfLines={1}
        >
          Wallet
        </Text>
      </VStack>
    </HStack>
  );
}

function RuleFromToParticipant({
  fromTo,
  qrs,
  organization,
  wallets,
  tags,
  participants,
  ...rest
}: RuleFromToBaseProps & {
  fromTo: Extract<PaymentVersionedRuleFromTo, { type: "participant" }>;
}) {
  const isAny = fromTo.id === "__any__";
  const { name, avatar, isDeleted } = useMemo(() => {
    if (isAny) {
      return {
        name: "Any worker",
        avatar: undefined,
        isDeleted: false,
      };
    }

    const participant = participants.find((p) => p.id === fromTo.id);

    if (!participant) {
      return {
        name: "Deleted worker",
        avatar: undefined,
        isDeleted: true,
      };
    }

    return {
      name: getName(participant.user),
      avatar: participant.user.avatarImage ?? undefined,
      isDeleted: false,
    };
  }, [participants, fromTo.id]);

  return (
    <HStack className="items-center justify-start gap-2" {...rest}>
      {isDeleted ? (
        <DeletedTooltipHandle type="participant" />
      ) : (
        <Avatar size="sm">
          <AvatarFallbackText>{name}</AvatarFallbackText>
          <AvatarImage source={{ uri: avatar }} />
        </Avatar>
      )}
      <VStack className="items-start flex-1 overflow-hidden">
        <Text
          size="sm"
          className="font-semibold text-typography-900 text-sm text-ellipsis whitespace-nowrap line-clamp-1"
          numberOfLines={1}
        >
          {name}
        </Text>
        <Text
          size="xs"
          className="text-ellipsis whitespace-nowrap line-clamp-1"
          numberOfLines={1}
        >
          {isAny ? "Any of the team" : "Worker"}
        </Text>
      </VStack>
    </HStack>
  );
}

export function RuleFromTo({
  fromTo,
  ...props
}: RuleFromToBaseProps & {
  fromTo: PaymentVersionedRuleFromTo;
}) {
  switch (fromTo.type) {
    case "organization": {
      return <RuleFromToOrganization {...props} fromTo={fromTo} />;
    }
    case "participant": {
      return <RuleFromToParticipant {...props} fromTo={fromTo} />;
    }
    case "tag": {
      return <RuleFromToTag {...props} fromTo={fromTo} />;
    }
    case "wallet": {
      return <RuleFromToWallet {...props} fromTo={fromTo} />;
    }
  }
}

export function getFromToRuleText({
  organization,
  wallets,
  tags,
  qrs,
  participants,
  fromTo,
}: RuleFromToDataProps & {
  fromTo: PaymentVersionedRuleFromTo;
}) {
  switch (fromTo.type) {
    case "organization":
      if (fromTo.qrId) {
        const qr = qrs.find((qr) => qr.id === fromTo.qrId);
        return qr
          ? `${organization.name} - ${qr.internalCode}`
          : `${organization.name} - Deleted QR-code`;
      }
      return organization.name;
    case "participant":
      if (!fromTo.id || fromTo.id === "__any__") {
        return "Any worker";
      }

      const participant = participants.find((p) => p.id === fromTo.id);
      return participant ? getName(participant.user) : "Deleted worker";
    case "tag":
      const unique = new Set([...tags.private, ...tags.public]);
      return unique.has(fromTo.tag) ? fromTo.tag : "Deleted tag";
    case "wallet":
      const wallet = wallets.find((w) => w.id === fromTo.id);
      if (!wallet) {
        return "Deleted wallet";
      }

      return wallet.name ?? wallet.currency;
  }
}

export type RestaurantTipsSharingItemProps = {
  data: PaymentRuletDTODataDistribution[keyof PaymentRuletDTODataDistribution][number] & {
    id: string;
  };
} & RuleFromToDataProps & {
    draggableNode?: ReactNode;
  };

export function RestaurantTipsSharingItem({
  data,
  draggableNode,
  ...props
}: RestaurantTipsSharingItemProps) {
  const content = (
    <>
      <Box className="w-2/5">
        <RuleFromTo {...props} fromTo={data.from} />
      </Box>
      <VStack className="w-1/5 items-center">
        <Text size="sm" className="text-typography-950">
          {data.percent}%
        </Text>
        <Icon as={MoveRight} size="lg" />
      </VStack>
      <Box className="w-2/5">
        <RuleFromTo {...props} fromTo={data.to} />
      </Box>
    </>
  );

  return (
    <Card size="sm" variant="filled" className="flex-row w-full items-center gap-4 overflow-hidden">
      {draggableNode ? (
        <>
          <Box className="w-fit">{draggableNode}</Box>
          <Box className="grow flex-row items-center">{content}</Box>
        </>
      ) : (
        content
      )}
    </Card>
  );
}
